import { useQuery } from '@tanstack/react-query';
import { usePublicClient } from 'wagmi';
import { erc20Abi, parseAbi, zeroAddress } from 'viem';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';
import { QueryOptions } from 'utils/useQueryUtils';
import { formatUnits } from 'utils/units';
import { getOracleDeployment } from 'sfi-oracle-sdk';

interface UseTokenPriceProps extends QueryOptions {
  tokenAddress: `0x${string}`;
  chainId: number;
}

export const useTokenPrice = (props: UseTokenPriceProps) => {
  const { tokenAddress, chainId, query } = props;
  const publicClient = usePublicClient({ chainId });
  const oracleDeployment = getOracleDeployment(chainId);
  const quoteAddress = oracleDeployment?.referenceAsset ?? zeroAddress;

  const useQueryOptions = {
    ...query,
    queryKey: queryKeys.tokenPrice({ tokenAddress, quoteAddress, chainId }),
    enabled: !!publicClient && !!oracleDeployment,
  };

  return useQuery({
    ...useQueryOptions,
    queryFn: async () => {
      if (!publicClient) throw new Error('Public client not found');
      if (!oracleDeployment) {
        throw new Error(`No oracle deployment found for chainId ${chainId}`);
      }

      const [data, quoteDecimals] = await Promise.all([
        publicClient.readContract({
          abi: parseAbi([
            'function getPrice(address base, address quote) view returns (uint256 price, uint256 oldestObservation)',
          ]),
          functionName: 'getPrice',
          address: oracleDeployment.referenceAssetOracle,
          args: [tokenAddress, oracleDeployment.referenceAsset],
          authorizationList: undefined,
        }),
        publicClient.readContract({
          abi: erc20Abi,
          address: oracleDeployment.referenceAsset,
          functionName: 'decimals',
          authorizationList: undefined,
        }),
      ]);

      const [price, oldestObservation] = data;

      return {
        price,
        decimals: quoteDecimals,
        formatedPrice: formatUnits(price, quoteDecimals),
        oldestObservation,
      };
    },
  });
};
